import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { BRAND, SHADOW } from '../theme/landingTheme';

const FEATURES = [
  'Profit calculator with all Walmart fees',
  'Price & review history charts',
  'Seller data',
  '2 Chrome extension installs',
  '2 mobile app installs',
  'Desktop app',
  '10 free stock alerts',
  'Email support',
];

export default function Pricing(props) {
  // Annual pre-selected: it is the recommended plan
  const [yearly, setYearly] = React.useState(1);

  const handleBilling = (event, value) => {
    if (value !== null) setYearly(value);
  };

  const startTrial = () => {
    props.setYearly(yearly);
    props.setEmailModal(true);
  };

  return (
    <Container
      id="pricing"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 4 },
      }}
    >
      <Box sx={{ width: { sm: '100%', md: '60%' }, textAlign: 'center' }}>
        <Typography component="h2" variant="h4" color="text.primary">
          One plan. Everything included.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          No tiers, no feature gates — every subscriber gets the full toolkit.
        </Typography>
      </Box>

      <ToggleButtonGroup
        value={yearly}
        exclusive
        onChange={handleBilling}
        aria-label="billing period"
        sx={{
          backgroundColor: BRAND.tint,
          borderRadius: '12px',
          p: 0.5,
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '10px !important',
            px: { xs: 2, sm: 3 },
            fontWeight: 700,
            textTransform: 'none',
            color: BRAND.ink,
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
          },
          '& .Mui-selected': {
            backgroundColor: '#fff !important',
            boxShadow: SHADOW.card,
          },
        }}
      >
        <ToggleButton value={0} aria-label="monthly billing">
          Monthly
        </ToggleButton>
        <ToggleButton value={1} aria-label="annual billing">
          Annual&nbsp;
          <Typography component="span" sx={{ color: BRAND.yellowDeep, fontWeight: 800, fontSize: '0.85rem' }}>
            2 months free
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      <Card
        sx={{
          p: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '440px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          border: '2px solid',
          borderColor: BRAND.yellow,
          boxShadow: SHADOW.card,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography component="h3" variant="h6">
              Marter — Full Access
            </Typography>
            {yearly === 1 && (
              <Chip
                label="Save $48 / year"
                size="small"
                sx={{ backgroundColor: BRAND.yellow, color: BRAND.ink, fontWeight: 700 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography component="h3" variant="h2">
              ${yearly === 1 ? '20' : '24'}
            </Typography>
            <Typography component="span" variant="h6" color="text.secondary">
              &nbsp;/ month
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {yearly === 1
              ? 'Billed annually — $240 per year (twelve months for the price of ten).'
              : 'Billed monthly. Switch to annual anytime to save $48.'}
          </Typography>

          <Divider sx={{ my: 2, opacity: 0.4 }} />

          {FEATURES.map((line) => (
            <Box
              key={line}
              sx={{ py: 0.75, display: 'flex', gap: 1.5, alignItems: 'center' }}
            >
              <CheckCircleRoundedIcon sx={{ width: 20, color: BRAND.yellowDeep }} />
              <Typography component="span" variant="subtitle2">
                {line}
              </Typography>
            </Box>
          ))}
        </CardContent>
        <CardActions sx={{ flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: BRAND.yellow,
              color: BRAND.ink,
              fontWeight: 700,
              py: 1.2,
              boxShadow: SHADOW.cta,
              '&:hover': { backgroundColor: BRAND.yellowDark },
            }}
            onClick={startTrial}
          >
            Start Free 7-Day Trial
          </Button>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            No charge until trial ends&nbsp;·&nbsp;Cancel anytime
          </Typography>
        </CardActions>
      </Card>

      <Typography
        variant="body2"
        sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'text.secondary' }}
        onClick={() => { window.location.href = 'manage-subscription'; }}
      >
        Already subscribed? Manage your subscription.
      </Typography>
    </Container>
  );
}
